import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RegistrationsPanel from "@/components/admin/RegistrationsPanel";
import { usePublishPageContext } from "@/components/admin/eos/PageContext";
import {
  ExecutivePage,
  PageHero,
  ThreeQuestions,
  ExecutiveSummary,
  QuietSection,
  DecisionCard,
  QuietEmptyState,
  type SummaryStat,
} from "@/components/executive/system";
import { Button } from "@/components/ui/button";
import { RefreshCw, Users, ChevronRight, AlertCircle } from "lucide-react";
import { useRegistrationsInsights } from "@/hooks/useRegistrationsInsights";


const money = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/* ------------------------------------------------------------------ */
/*  Registrations — Executive Office rebuild.                          */
/*                                                                     */
/*  Answers, in five seconds:                                          */
/*    • What changed?    → new paid + new interest since last week.    */
/*    • Needs attention? → failed/pending payments, unreviewed rows,   */
/*                         and interest forms whose class no longer     */
/*                         resolves. General Interest is NOT a problem. */

/*                                                                     */
/*  The existing RegistrationsPanel (period → class → schedule option) */
/*  is preserved verbatim as the "Detailed records" drill-down so all  */
/*  edit / delete / assign / period-tab logic remains available.       */
/* ------------------------------------------------------------------ */

export default function RegistrationsPage() {
  const [params, setParams] = useSearchParams();
  // `registration=` is the older link shape used by the financial ledger and
  // the Waiting page; it carries the same canonical registration UUID.
  // `highlight=` (Unified Dashboard "Registrations to Review") carries the
  // same class_payments row id, so it maps onto the payment focus behavior.
  const focusPayment =
    params.get("focusPayment") ?? params.get("registration") ?? params.get("highlight");
  const focusInterest = params.get("focusInterest");
  // `class=` (Unified Dashboard + Operations Board) carries a class slug and
  // opens the paid-registrations group for that class rather than one record.
  const focusClass = params.get("class");

  usePublishPageContext({
    selection: focusPayment
      ? { kind: "registration", id: focusPayment, label: `Registration ${focusPayment.slice(0, 8)}`, pronoun: "it" }
      : focusInterest
        ? { kind: "interest_form", id: focusInterest, label: `Interest form ${focusInterest.slice(0, 8)}`, pronoun: "it" }
        : null,
    note: focusPayment || focusInterest ? "A specific record is focused via URL." : undefined,
  });

  const { loading, insights, refresh } = useRegistrationsInsights();

  /* ---- Three Questions -------------------------------------------- */
  const whatChanged = useMemo(() => {
    if (loading) return "Reading the ledger…";
    const parts: string[] = [];
    if (insights.newThisWeek.payments)
      parts.push(`${insights.newThisWeek.payments} new paid registration${insights.newThisWeek.payments === 1 ? "" : "s"}`);
    if (insights.newThisWeek.interest)
      parts.push(`${insights.newThisWeek.interest} new interest form${insights.newThisWeek.interest === 1 ? "" : "s"}`);
    if (parts.length === 0) return "No new registrations or interest in the last seven days.";
    const top = insights.topClasses[0];
    const tail = top && top.count > 1 ? ` — most in ${top.title}.` : ".";
    return `In the last seven days: ${parts.join(" and ")}${tail}`;
  }, [loading, insights]);

  const needsAttention = useMemo(() => {
    const bits: string[] = [];
    if (insights.needsAttention.failed)
      bits.push(`${insights.needsAttention.failed} payment${insights.needsAttention.failed === 1 ? "" : "s"} failed`);
    if (insights.needsAttention.pending)
      bits.push(`${insights.needsAttention.pending} awaiting completion`);
    if (insights.needsAttention.unresolvedInterest)
      bits.push(`${insights.needsAttention.unresolvedInterest} interest form${insights.needsAttention.unresolvedInterest === 1 ? "" : "s"} need${insights.needsAttention.unresolvedInterest === 1 ? "s" : ""} a class match`);
    if (insights.needsAttention.unreadPayments + insights.needsAttention.unreadInterest > 0)
      bits.push(`${insights.needsAttention.unreadPayments + insights.needsAttention.unreadInterest} unreviewed`);
    if (bits.length === 0) return "Every family is squared away.";
    return bits.join(" · ");
  }, [insights]);

  const focusItems = useMemo(() => buildFocus(insights), [insights]);

  const doNext = useMemo(() => {
    if (focusItems.length === 0) return "Take a breath — the roster is calm.";
    return focusItems[0].decision;
  }, [focusItems]);

  const attentionCount =
    insights.needsAttention.failed +
    insights.needsAttention.pending +
    insights.needsAttention.unresolvedInterest +
    insights.needsAttention.unreadPayments +
    insights.needsAttention.unreadInterest;

  /* ---- Executive Summary strip ------------------------------------ */
  const summary: SummaryStat[] = [
    { label: "Paid this month", value: money(insights.totals.revenueThisMonth) },
    { label: "New (7 days)", value: insights.newThisWeek.payments + insights.newThisWeek.interest, hideIfZero: true, hint: "registrations + interest" },
    { label: "Awaiting payment", value: insights.needsAttention.pending, hideIfZero: true },
    { label: "Payments failed", value: insights.needsAttention.failed, hideIfZero: true },
    { label: "Needs review", value: insights.needsAttention.unresolvedInterest, hideIfZero: true, hint: "interest forms without a class match" },
    { label: "Unreviewed", value: insights.needsAttention.unreadPayments + insights.needsAttention.unreadInterest, hideIfZero: true },
  ];


  return (
    <ExecutivePage section="registrations" footerNote="Silver Sage Homeschool Hub · Registrations">
      <UnseenReviewBar
        badge="registrations"
        note="Marking these read only clears the sidebar bubble — no registration, payment or refund is changed."
      />

      <PageHero
        eyebrow={<><span aria-hidden>🌿</span><span>Registrations</span></>}
        title={<>Families &amp; sign-ups</>}
        subtitle={<>Every paid registration and every interest form, arranged the way your day flows.</>}
        actions={
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <ThreeQuestions
        whatChanged={whatChanged}
        needsAttention={needsAttention}
        doNext={doNext}
        attentionCount={attentionCount}
      />

      <ExecutiveSummary stats={summary} />

      {/* Today's Focus — at most three decisions. */}
      <QuietSection
        eyebrow="Today's focus"
        title="Three decisions worth your time"
        description="Everything else can wait. If nothing is here, the desk is calm."
        paper={false}
      >
        {focusItems.length === 0 ? (
          <QuietEmptyState
            icon={<Users className="h-6 w-6 mx-auto" />}
            message="No urgent registration work today."
            detail="New sign-ups and interest forms will surface here as they arrive."
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {focusItems.map((item) => (
              <DecisionCard
                key={item.id}
                decision={item.decision}
                because={item.because}
                phase={item.phase}
                primaryLabel={item.primaryLabel}
                onPrimary={item.onPrimary}
                evidence={item.evidence}
              />
            ))}
          </div>
        )}
      </QuietSection>

      {/* Detailed records — the existing panel, verbatim. All edit / delete /
          assign / period-tab / weekday-repair logic is preserved. */}
      <QuietSection
        eyebrow="Detailed records"
        title="View families"
        description="Semester → Class → Schedule option. Every row is editable; May 2026 and other past periods live in the Archived / Historical menu."
        right={
          <Link
            to="/admin/reports"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            View payment history <ChevronRight className="h-3 w-3" />
          </Link>
        }
        paper={false}
      >
        <div data-panel="registrations-detail" className="paper-card p-3 md:p-4 transition-shadow">
          <RegistrationsPanel
            focusPayment={focusPayment}
            focusInterest={focusInterest}
            focusClass={focusClass}
            onFocusHandled={() => {
              const next = new URLSearchParams(params);
              next.delete("focusPayment");
              next.delete("focusInterest");
              next.delete("registration");
              next.delete("highlight");
              next.delete("class");
              setParams(next, { replace: true });
            }}
          />
        </div>
      </QuietSection>
    </ExecutivePage>
  );
}

/* ------------------------------------------------------------------ */
/*  Today's Focus builder                                              */
/*                                                                     */
/*  Ranked in priority order. Every item ships with:                   */
/*    - A decision-verb (never a noun-only label)                      */
/*    - A one-sentence "because" the Director can read at a glance     */
/*    - An evidence panel (Show the math) with the underlying rows     */
/* ------------------------------------------------------------------ */

interface FocusItem {
  id: string;
  decision: string;
  because: string;
  phase: "needs_care" | "waiting" | "growing";
  primaryLabel: string;
  onPrimary: () => void;
  evidence?: React.ReactNode;
}

function buildFocus(
  insights: ReturnType<typeof useRegistrationsInsights>["insights"],
): FocusItem[] {
  const items: FocusItem[] = [];

  // 1. Failed payments — highest priority.
  if (insights.needsAttention.failed > 0) {
    const n = insights.needsAttention.failed;
    items.push({
      id: "failed",
      decision: n === 1 ? "Send payment reminder to one family" : `Follow up on ${n} failed payments`,
      because: "Stripe reported these attempts as failed. A short reminder usually clears them.",
      phase: "needs_care",
      primaryLabel: "Review failed payments",
      onPrimary: () => scrollToPanel(),
      evidence: (
        <ul className="list-disc pl-5 space-y-1">
          {insights.records.failedPayments.slice(0, 8).map((p) => (
            <li key={p.id}>
              {p.adult_name || p.email} · {insights.resolveTitle(p.class_id)} · {money(p.amount)}
            </li>
          ))}
        </ul>
      ),
    });
  }

  // 2. Interest rows whose class couldn't be resolved at all. Rows without a
  //    schedule_option/weekday are valid "General Interest" and never appear
  //    here — the canonical resolver treats them as complete records.
  if (insights.needsAttention.unresolvedInterest > 0) {
    const n = insights.needsAttention.unresolvedInterest;
    items.push({
      id: "unresolved-interest",
      decision: `Match ${n} interest form${n === 1 ? "" : "s"} to a class`,
      because: "These submissions reference a class that no longer exists in the catalog. Assign the right class or archive.",
      phase: "waiting",
      primaryLabel: "Open interest forms",
      onPrimary: () => scrollToPanel("interest"),
      evidence: (
        <ul className="list-disc pl-5 space-y-1">
          {insights.records.unresolvedInterest.slice(0, 8).map((i) => (
            <li key={i.id}>
              {i.adult_name} · {insights.resolveTitle(i.class_id)}
            </li>
          ))}
        </ul>
      ),
    });
  }


  // 3. Unreviewed new arrivals.
  const unreadTotal =
    insights.needsAttention.unreadPayments + insights.needsAttention.unreadInterest;
  if (unreadTotal > 0 && items.length < 3) {
    items.push({
      id: "unreviewed",
      decision: `Review ${unreadTotal} new registration${unreadTotal === 1 ? "" : "s"}`,
      because:
        insights.needsAttention.unreadPayments && insights.needsAttention.unreadInterest
          ? `${insights.needsAttention.unreadPayments} paid and ${insights.needsAttention.unreadInterest} interest.`
          : insights.needsAttention.unreadPayments
            ? "Paid registrations that haven't been marked reviewed."
            : "Interest forms that haven't been marked reviewed.",
      phase: "growing",
      primaryLabel: insights.needsAttention.unreadPayments
        ? "Open paid registrations"
        : "Open interest forms",
      onPrimary: () =>
        scrollToPanel(insights.needsAttention.unreadPayments ? "payments" : "interest"),
    });
  }

  // 4. Pending — surface only if we have room.
  if (insights.needsAttention.pending > 0 && items.length < 3) {
    items.push({
      id: "pending",
      decision: `Check ${insights.needsAttention.pending} checkout${insights.needsAttention.pending === 1 ? "" : "s"} still in progress`,
      because: "Started, not yet confirmed by Stripe. May have been abandoned or delayed.",
      phase: "waiting",
      primaryLabel: "Review pending",
      onPrimary: () => scrollToPanel(),
    });
  }

  return items.slice(0, 3);
}

// Note: title resolution goes through insights.resolveTitle, which uses the
// canonical @/lib/classLabels resolver (same as RegistrationsPanel + MAVIS).


/** Scroll the detailed panel into view. Tabs stay whatever the user picked. */
function scrollToPanel(_tab?: "payments" | "interest") {
  const el = document.querySelector("[data-panel='registrations-detail']") as HTMLElement | null;
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Flash a subtle highlight so the focus lands.
    el.classList.add("ring-2", "ring-sage/40");
    setTimeout(() => el.classList.remove("ring-2", "ring-sage/40"), 1400);
  }
  // No-op for _tab; the RegistrationsPanel manages its own Tabs state and
  // preserves whatever tab the Director already had open. Forcing a switch
  // would violate the "never make the administrator lose their place" rule.
  void AlertCircle; // keep lucide import alive across code paths
}
